import { FC, useMemo } from "react";
import { CircularProgress, Divider, InputAdornment, Stack, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { ReduxState } from "ra-core";
import SearchIcon from '@/icons/Search';
import SearchCriteriaChips, { ChipItem } from "../input/SearchCriteriaChips";
import { PopTimeRangeKind, displayTimeRangeLabel, usePopTimeRangePicker } from "../input/PopTimeRangePicker";
import { useOfflineFileTableSearchContext } from "./OfflineFileTableSearchProvider";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { PopBaseFilter } from "../input/PopAutocompleteFilter";
import { getUserProfile } from "@/reducers/userProfile";


const OfflineFileSearchRegion: FC = () => {
  const format = useUserTimeFormat()
  const userProfile = useSelector(getUserProfile)
  const { states, setSearchTime, setCompleted } = useOfflineFileTableSearchContext()
  const { PopTimeRangePicker, open, states: tStates } = usePopTimeRangePicker({
    label: 'Time Range',
    onChange: ({kind, start, end}) => {
      setSearchTime(start, end)
    },
    defaultKind: {} as any,
    PopoverProps: {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: 'right' },
    },
    ianaTimeZone: userProfile?.ianaTimeZone,
  })
  const criteriaChips = useMemo(() => {
    const arr = [] as ChipItem[][]
    if (states.completed != null) {
      arr.push([{id: 'c', label: states.completed ? 'Completed' : 'Not Completed', content: '', delete: () => setCompleted(null)}])
    }
    if (tStates) {
      if (tStates.start || tStates.end) {
        arr.push([{
          id: 'tr',
          label: 'Time Range',
          content: 
            tStates.kind !== PopTimeRangeKind.Custom
            ? (<code style={{ textTransform: 'capitalize', marginLeft: '8px' }}>within {displayTimeRangeLabel(tStates.kind)}</code> as any)
            : !tStates.end ? `Starts from ${format(tStates.start!, 'Pp')}`
            : !tStates.start ? `Up until ${format(tStates.end!, 'Pp')}`
            : `${format(tStates.start, 'Pp')} - ${format(tStates.end, 'Pp')}`
            ,
          delete: () => open(),
        }])
      }
    }
    return arr
  }, [format, open, setCompleted, states.completed, tStates])

  return (
    <>
      <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ p: 2, pl: 4, m: -1}}>
        <SearchNameField />
        <PopCompletionFilter />
        {PopTimeRangePicker}
      </Stack>
      <Divider />
      <SearchCriteriaChips criteriaChips={criteriaChips} />
    </>
  )
}

export default OfflineFileSearchRegion;


const SearchNameField : FC = () => {
  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)
  const {
    states: {fileName},
    searchFileName,
  } = useOfflineFileTableSearchContext()

  return (
    <TextField 
      id="offlinefiletable-search-name"
      placeholder='Search by File Name'
      fullWidth
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
          </InputAdornment>
        )
      }}
      onChange={(e) => searchFileName(e.target.value)}
      value={fileName}
      variant="outlined"
      sx={{ width: 280, mr: 'auto' }}
    />
  )
}


const completionOptions = [
  {key: 1, text: '[None]', value: null as boolean|null},
  {key: 2, text: 'Completed', value: true as boolean|null},
  {key: 3, text: 'Not Completed', value: false as boolean|null},
]
const PopCompletionFilter : FC = () => {
  const { setCompleted } = useOfflineFileTableSearchContext()
  return (
    <PopBaseFilter 
      label="Completed or not"
      id="completion-picker"
      options={completionOptions}
      onChange={(event, option) => {
        if (option) {
          setCompleted((option as any).value)
        }
      }}
    />
  )
}
